import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import logo from "@/assets/logo.jpeg";
import { CONTACT } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  const navLinks = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.story"), id: "story" },
    { label: t("nav.products"), id: "products" },
    { label: t("nav.markets"), id: "markets" },
    { label: t("nav.values"), id: "values" },
    { label: t("nav.faq"), id: "faq" },
    { label: t("nav.contact"), id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-[#40916c]/10 shadow-[0_4px_30px_rgba(45,106,79,0.06)]"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 h-[76px] flex items-center justify-between">
          <button
            onClick={() => go("hero")}
            className="flex items-center gap-3 group text-left"
            data-testid="nav-logo"
          >
            <img
              src={logo}
              alt="Gupta Enterprises logo"
              className="h-11 w-11 rounded-full object-cover ring-1 ring-[#40916c]/15 group-hover:ring-[#40916c]/40 transition-all"
            />
            <span className="hidden sm:flex flex-col leading-none text-left">
              <span className="font-display font-semibold text-[15px] tracking-tight text-[#2d6a4f]">
                Gupta Enterprises
              </span>
              <span className="font-body text-[10px] tracking-[0.25em] uppercase text-[#40916c]/70">
                Vasavi Gold
              </span>
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="font-body text-[14px] text-[#3a4a41] hover:text-[#40916c] transition-colors relative group"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#40916c] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Language Switcher Pill */}
            <div className="flex items-center gap-1 rounded-full border border-[#40916c]/30 p-1 bg-white/70 backdrop-blur-md shadow-sm">
              <button
                onClick={() => setLang("en")}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all ${
                  lang === "en"
                    ? "bg-[#40916c] text-white shadow-sm"
                    : "text-[#3a4a41] hover:text-[#40916c]"
                }`}
                data-testid="lang-toggle-en"
                aria-label="Switch to English"
              >
                EN
              </button>
              <button
                onClick={() => setLang("te")}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all ${
                  lang === "te"
                    ? "bg-[#40916c] text-white shadow-sm"
                    : "text-[#3a4a41] hover:text-[#40916c]"
                }`}
                data-testid="lang-toggle-te"
                aria-label="Switch to Telugu"
              >
                తెలుగు
              </button>
            </div>

            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              onClick={() => go("contact")}
              className="hidden md:inline-flex items-center rounded-full bg-[#40916c] hover:bg-[#2d6a4f] text-white font-body text-[14px] font-medium px-5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#40916c]/25"
              data-testid="nav-enquiry-btn"
            >
              {t("hero.enquiryBtn")}
            </a>
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-[#40916c]/20 text-[#2d6a4f]"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-[#40916c]/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              <div className="flex items-center justify-between py-2 border-b border-[#40916c]/10 mb-2">
                <span className="flex items-center gap-2 font-body text-xs text-[#5b6b62]">
                  <Globe size={14} /> {t("nav.language")}
                </span>
                <div className="flex items-center gap-1 rounded-full border border-[#40916c]/30 p-1 bg-white">
                  <button
                    onClick={() => setLang("en")}
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      lang === "en" ? "bg-[#40916c] text-white" : "text-[#3a4a41]"
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => setLang("te")}
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      lang === "te" ? "bg-[#40916c] text-white" : "text-[#3a4a41]"
                    }`}
                  >
                    తెలుగు
                  </button>
                </div>
              </div>

              {navLinks.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-body text-[#2d6a4f] py-3 border-b border-[#40916c]/5"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 text-center rounded-full bg-[#40916c] text-white font-body font-medium px-6 py-3"
                data-testid="nav-mobile-enquiry-btn"
              >
                {t("hero.enquiryBtn")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
